import CONSTANTS from 'utils/constants';
import _ from 'ckLodash';
import biUtils from 'utils/biUtils';
import elementUtils from 'utils/elementUtils';
import findElementUtils from 'utils/findElementUtils';
import rangeUtils from 'utils/rangeUtils';
import utils from 'utils/utils';

function getSelectedRange(editor) {
    var sel = editor.getSelection();
    return sel && sel.getRanges()[0];
}

function getSelectedHtml(editor) {
    var selectedHtml = editor.getSelectedHtml();

    if (_.isNil(selectedHtml)) {
        setSelectionOnEnd(editor);
        selectedHtml = editor.getSelectedHtml();
    }

    return selectedHtml;
}

function noNeedToEnlarge(editor) {
    try {
        var children = getSelectedHtml(editor).$.children;
        var child = children[children.length - 1];
        return child.tagName === 'BR';
    } catch (e) {
        return false;
    }
}

function enlargeSelectionToBlock(editor) {
    if (noNeedToEnlarge(editor)) {
        return;
    }

    var range = getSelectedRange(editor);
    if (!range) {
        return;
    }

    range.enlarge(CKEDITOR.ENLARGE_BLOCK_CONTENTS);
    try {
        // this sometimes fail IE
        range.select();
    } catch (e) {//eslint-disable-line no-empty
    }
}

function hasSelectedRange(editor) {
    return !!getSelectedRange(editor);
}

function isStartBlockTextFullySelected(editor, startBlock, ignoreGuard) {
    //note that this does not take brs into account. this might be a problem
    var range = getSelectedRange(editor).clone();
    var firstTextNode = findElementUtils.findFirstNonEmptyTextNode(editor, startBlock);
    if (!firstTextNode) {
        return true;
    }

    range.setStart(firstTextNode, 0);

    var rangeText = rangeUtils.getText(range);
    var selectedText = getSelectedText(editor);
    var startBlockText = startBlock.$.textContent;

    if (ignoreGuard) {
        rangeText = utils.removeGuardsChars(rangeText);
        selectedText = utils.removeGuardsChars(selectedText);
        startBlockText = utils.removeGuardsChars(startBlockText);
    }

    if (selectionEndsInSameBlockAsElement(editor, startBlock)) {
        return selectedText === startBlockText;
    }
    // if <p>a{a</p><p>a}</p> this still works cuz rangeText will be aaa as oposed to selectedText which will be aa
    return _.startsWith(selectedText, rangeText);
}

function getSelectedText(editor) {
    var selectedHtml = getSelectedHtml(editor);
    return selectedHtml ? selectedHtml.$.textContent : '';
}

function findLastTextNode(editor, element) {
    var node,
        result = null;
    var walker = elementUtils.getWalkerForElement(editor, element);

    while (node = walker.previous()) {
        if (node.type === CKEDITOR.NODE_TEXT) {
            result = node;
            walker.end();
        }
    }

    return result;
}

function isSelectedTextEmpty(editor) {
    //ignore our or ck's invisible temporary guards
    var selectedText = getSelectedHtml(editor).$.textContent;
    return utils.removeGuardsChars(selectedText).length === 0;
}

function setSelectionBetweenElements(editor, firstElem, lastElem) {
    var range = new CKEDITOR.dom.range(editor.editable());
    range.setStartAt(firstElem, CKEDITOR.POSITION_AFTER_START);
    range.setEndAt(lastElem, CKEDITOR.POSITION_BEFORE_END);
    range.select();
}

function selectionEndsInSameBlockAsElement(editor, element) {
    var elementBlock = utils.getBlockParent(new CKEDITOR.dom.elementPath(element));
    var endSelectionBlock = utils.getBlockParent(getSelectedRange(editor).endPath());
    return elementBlock.equals(endSelectionBlock);
}

function countBrTagsBeforeCursor(editor, startElement) {
    startElement = startElement || editor.editable();
    var range = new CKEDITOR.dom.range(editor.editable());
    range.selectNodeContents(startElement);
    var selectedRange = getSelectedRange(editor);
    range.setEnd(selectedRange.startContainer, selectedRange.startOffset);
    return rangeUtils.countBrInRange(range);
}

function createBookmark(editor, forceCreate) {
    var range = getSelectedRange(editor);
    if (forceCreate) {
        return createTaggedBookmark(range);
    }
    if (range.startContainer.type === CKEDITOR.NODE_TEXT && range.endContainer.type === CKEDITOR.NODE_TEXT) {
        if (range.startContainer.equals(range.endContainer) && range.startContainer.$.textContent === 0) {
            //range anchored in empty text node is not to be considered safe
            return createTaggedBookmark(range);
        }
        return null;
    }
    return createTaggedBookmark(range);
}

function createTaggedBookmark(range) {
    const bookmark = range.createBookmark();

    if (bookmark.startNode) {
        bookmark.wixStartNodeId = _.uniqueId();
        bookmark.startNode.setAttribute(`wix-bookmark-${bookmark.wixStartNodeId}`, '');
    }

    if (bookmark.endNode) {
        bookmark.wixEndNodeId = _.uniqueId();
        bookmark.endNode.setAttribute(`wix-bookmark-${bookmark.wixEndNodeId}`, '');
    }

    return bookmark;
}

function moveToBookmark(editor, bookmark) {
    if (!bookmark) {
        return;
    }
    updateBookmarkNodesIfNeeded(editor, bookmark);

    if (!bookmark.startNode) {
        // we should never get here, but in case we somehow deleted the start bookmark, we report an error
        biUtils.error.unableToRestoreSelection();
        return;
    }
    const range = getSelectedRange(editor);
    range.moveToBookmark(bookmark);
    range.select();
}

// when we manipulate the dom as html text, the bookmark nodes's references are lost. this is how we restore them
function updateBookmarkNodesIfNeeded(editor, bookmark) {
    if (bookmark.startNode && !editor.editable().contains(bookmark.startNode)) {
        const restoredBookmark = editor.editable().$.querySelector(`[wix-bookmark-${bookmark.wixStartNodeId}]`);

        if (restoredBookmark) {
            const ckDomNode = new CKEDITOR.dom.element(restoredBookmark);
            bookmark.startNode = ckDomNode;
        }
    }

    if (bookmark.endNode && !editor.editable().contains(bookmark.endNode)) {
        const restoredBookmark = editor.editable().$.querySelector(`[wix-bookmark-${bookmark.wixEndNodeId}]`);

        if (restoredBookmark) {
            const ckDomNode = new CKEDITOR.dom.element(restoredBookmark);
            bookmark.endNode = ckDomNode;
        }
    }
}

function rangeStartsOnBody(range, editor) {
    return range.startContainer.equals(editor.editable());
}

function rangeEndsOnBody(range, editor) {
    return range.endContainer.equals(editor.editable());
}

function fixRangeStart(range) {
    //we assume that there must be a first text node on every text block and it must be properly position
    //for either there is such a text node or there must be a guard that we inserted exactly to be able to make decisions
    //such as this one(where to move selection to
    var firstTextNode = rangeUtils.findFirstNonEmptyTextNode(range);
    if (firstTextNode) {
        range.setStart(firstTextNode, 0);
    } else {
        //the more correct solution would be to find the first br in first text node if it exists, better chance to preserve style
        //the even more correct solution would be to never get to the situation that there is no guard, it would show us the way
        var firstTextBlock = rangeUtils.findFirstTextBlock(range);
        range.setStart(firstTextBlock, 0);
    }
}

function fixRangeEnd(range) {
    var lastTextNode = rangeUtils.findLastNonEmptyTextNode(range);
    if (lastTextNode) {
        range.setEnd(lastTextNode, lastTextNode.$.textContent.length);
    } else {
        var lastTextBlock = rangeUtils.findLastTextBlock(range);
        range.setEnd(lastTextBlock, lastTextBlock.getChildCount());
    }
}

function fixCollapsedRangeOnBody(range, editor) {
    if (rangeStartsOnBody(range, editor)) {
        var newRange = range.clone();
        if (range.startOffset === 0) {
            newRange.setEnd(editor.editable(), editor.editable().getChildCount());
            fixRangeStart(newRange);
            newRange.collapse(true);
        } else {
            newRange.setStart(editor.editable(), 0);
            fixRangeEnd(newRange);
            newRange.collapse();
        }
        newRange.select();
    }
}

function fixSelectionOnBodyIfNeeded(editor) {
    if (utils.isFF() || utils.isIE()) {
        fixSelectionOnBody(editor);
    }
}

function fixSelectionOnBody(editor) {
    var range = getSelectedRange(editor);
    var isRangeModified;

    if (range.collapsed) {
        fixCollapsedRangeOnBody(range, editor);
        return;
    }

    if (rangeStartsOnBody(range, editor)) {
        isRangeModified = true;
        fixRangeStart(range);
    }

    if (rangeEndsOnBody(range, editor)) {
        isRangeModified = true;
        fixRangeEnd(range);
    }

    if (isRangeModified) {
        range.select();
    }
}

function fixSelectionOnPlaceHolder(editor) {
    var range = getSelectedRange(editor);
    if (!range) {
        return;
    }
    //todo don't check for contentEditable, verify data-wix-placeholder instead
    const placeHolderElement = range.startPath().contains(elem => elem.getAttribute('data-wix-placeholder'));
    const enclosedNode = range.getEnclosedNode();
    if (placeHolderElement && (!enclosedNode || placeHolderElement.$.textContent !== enclosedNode.$.textContent)) {
        range.selectNodeContents(placeHolderElement);
        range.select();
    }
}

function isFullBlockSelectionAnchoringOnNextBlock(editor) {
    var selection = editor.getSelection();
    var range = selection.getRanges()[0];
    var selectedText = selection
        .getSelectedText()
        .replace(/\n/g, '')
        .replace(CONSTANTS.CK_GUARD, '');

    var startBlock = range.startPath().block;

    return !_.isEmpty(selectedText) &&
        isStartBlockTextFullySelected(editor, startBlock) &&
        selectedText === startBlock.getText().replace(/\n/g, '').replace(CONSTANTS.CK_GUARD, '');
}

function isMultipleBlockSelection(editor) {
    if (!editor.getSelectedHtml()) {
        return false;
    }

    var range = editor.getSelection().getRanges()[0];
    if (!range || !range.startPath().block) {
        return false;
    }

    var startAndEndBlocks = rangeUtils.getStartAndEndBlock(range);
    if (_.isEqual(startAndEndBlocks.startBlock.getUniqueId(), startAndEndBlocks.endBlock.getUniqueId())) {
        return false;
    }

    //If we have more than one block in selection but we are only anchored in next block (triple click) it should be like one block selection
    return !isFullBlockSelectionAnchoringOnNextBlock(editor);
}

function forEachSelectedElement(editor, callback) {
    var range = getSelectedRange(editor);
    var domWalker = new CKEDITOR.dom.walker(range);
    while (domWalker.next()) {
        callback(domWalker.current);
    }
}

function getTextNodesInSelection(editor) {
    return rangeUtils.getTextNodesInSelection(getSelectedRange(editor));
}

function getSelectedBlocks(editor, includePartiallySelectedBlocks) {
    var range = getSelectedRange(editor);
    return rangeUtils.getBlocks(range, includePartiallySelectedBlocks);
}

function getSelectedLists(editor, includePartiallySelectedLists) {
    var range = getSelectedRange(editor);
    return rangeUtils.getLists(range, includePartiallySelectedLists);
}

function getSelectedListItems(editor, includeStartItem = true) {
    return rangeUtils.getListItems(getSelectedRange(editor), includeStartItem);
}

function setSelectionOnStart(editor) {
    const firstTextNode = findElementUtils.findFirstNonEmptyTextNode(editor);
    if (firstTextNode) {
        rangeUtils.createRange(editor.editable(), firstTextNode, 0).select();
    }
}

function setSelectionOnEnd(editor) {
    const lastTextNode = findLastTextNode(editor, editor.editable());
    if (lastTextNode) {
        rangeUtils.createRange(editor.editable(), lastTextNode, lastTextNode.getText().length).select();
    }
}

function setSelectionOnAll(editor) {
    const range = new CKEDITOR.dom.range(editor.editable());
    const firsTextNode = findElementUtils.findFirstNonEmptyTextNode(editor);
    const lastTextNode = findLastTextNode(editor, editor.editable());

    if (firsTextNode && lastTextNode) {
        range.setStart(firsTextNode, 0);
        range.setEnd(lastTextNode, lastTextNode.getText().length);
        range.select();
    }
}

function setSelectionAt(editor, cursorLocation) {
    switch (cursorLocation) {
        case CONSTANTS.CURSOR_LOCATIONS.START:
            setSelectionOnStart(editor);
            break;
        case CONSTANTS.CURSOR_LOCATIONS.ALL:
            setSelectionOnAll(editor);
            break;
        case CONSTANTS.CURSOR_LOCATIONS.END:
            setSelectionOnEnd(editor);
            break;
    }
}

function isAllSelected(editor) {
    if (!isSelectedTextEmpty(editor)) {
        var range = getSelectedRange(editor);
        const currentSelectedText = rangeUtils.getText(range);
        const allText = editor.editable().getText();
        return currentSelectedText === allText;
    }
    return false;
}

export default {
    createBookmark,
    countBrTagsBeforeCursor,
    enlargeSelectionToBlock,
    findLastTextNode,
    fixSelectionOnBodyIfNeeded,
    fixSelectionOnBody,
    fixSelectionOnPlaceHolder,
    forEachSelectedElement,
    getSelectedBlocks,
    getSelectedListItems,
    getSelectedLists,
    getSelectedRange,
    getSelectedText,
    getTextNodesInSelection,
    hasSelectedRange,
    isSelectedTextEmpty,
    isStartBlockTextFullySelected,
    moveToBookmark,
    selectionEndsInSameBlockAsElement,
    setSelectionBetweenElements,
    isMultipleBlockSelection,
    setSelectionAt,
    getSelectedHtml,
    isAllSelected
};
